import React from "react";
import { DateTime } from "luxon";
import type { DurationLike } from "luxon";
import { Controller, useFormContext } from "react-hook-form";
import type { UseControllerProps } from "react-hook-form/dist/types/controller";
import DatePickerInput from "components/DatePickerInput";
import type { DatePickerPreset } from "components/DatePickerInput/DatePickerInput";

type PresetDuration = {
  label: string;
  duration: DurationLike;
};

type PresetDurations = "OneWeek" | "OneMonth" | "ThreeMonths" | "SixMonths" | "OneYear" | "TwoYears" | "ThreeYears";

export const presetDurations: Record<PresetDurations, PresetDuration> = {
  OneWeek: {
    label: "1 week",
    duration: { day: 7 },
  },
  OneMonth: {
    label: "1 month",
    duration: { months: 1 },
  },
  ThreeMonths: {
    label: "3 months",
    duration: { months: 3 },
  },
  SixMonths: {
    label: "6 months",
    duration: { months: 6 },
  },
  OneYear: {
    label: "1 year",
    duration: { years: 1 },
  },
  TwoYears: {
    label: "2 years",
    duration: { years: 2 },
  },
  ThreeYears: {
    label: "3 years",
    duration: { years: 3 },
  },
};

export const createDatePickerPresets = (
  startDate: DateTime,
  durations: Array<PresetDuration>
): Array<DatePickerPreset> => {
  return durations.map((p) => ({
    label: p.label,
    getDate: () => startDate?.plus(p.duration) ?? DateTime.utc().plus(p.duration),
  }));
};

// Using any type since react form hooks doesn't play nice with Luxon's DateTime type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FormDatePickerProps extends UseControllerProps<any> {
  minDate?: DateTime;
  maxDate?: DateTime;
  label: string;
  presets?: DatePickerPreset[];
  showLocalConversion: boolean;
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
}

export function FormDatePicker(props: FormDatePickerProps) {
  const {
    name,
    rules,
    shouldUnregister,
    defaultValue,
    presets,
    minDate,
    maxDate,
    label,
    showLocalConversion,
    disabled,
    variant,
  } = props;
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue ?? null}
      control={control}
      render={({ field: { ref, ...rest }, fieldState }) => (
        <DatePickerInput
          {...rest}
          inputRef={ref}
          presets={presets}
          required={!!rules?.required}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          showLocalConversion={showLocalConversion}
          disabled={disabled}
          variant={variant}
        />
      )}
    />
  );
}
